<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ExternalLink, LifeBuoy } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import Sheet from '@/components/ui/sheet/Sheet.vue'
import SheetContent from '@/components/ui/sheet/SheetContent.vue'
import SheetDescription from '@/components/ui/sheet/SheetDescription.vue'
import SheetTitle from '@/components/ui/sheet/SheetTitle.vue'
import ChatComposerOverlay from '@/components/customUi/Chat/ChatComposerOverlay.vue'
import { navigateTo } from '@/router/utils'
import { Routes } from '@/router/routes'
import { trackAssistantEvent } from '@/lib/assistantTelemetry'
import { useResearchChat, type ChatMessage } from '@/views/ResearchChat/useResearchChat'
import ResearchChatMessage from '@/views/ResearchChat/ResearchChatMessage.vue'
import { ASSISTANT_MODES } from '@/feature/tutorialAgent/assistantModes'
import { useTranslateKey } from '@/lib/utils'
import { storeEditorPrefill } from '@/lib/editorPrefillTransport'

const open = defineModel<boolean>('open', { required: true })

const route = useRoute()
const researchChat = useResearchChat()
const { translateKey } = useTranslateKey()
const messageInput = ref('')
const messagesContainerRef = ref<HTMLElement | null>(null)
const isFullAssistantRoute = computed(() => route.name === Routes.ASSISTANT)
const assistantMode = ASSISTANT_MODES.RESEARCH
const panelModeSource = `panel-${assistantMode}`

const activeMessages = computed<ChatMessage[]>(() => {
  return researchChat.messages.value
})

const isSending = computed(() => {
  return researchChat.isSending.value
})

const canSend = computed(() => {
  return messageInput.value.trim().length > 0 && !isSending.value
})

const displayMessages = computed(() => activeMessages.value)

const hasMessages = computed(() => displayMessages.value.length > 0)

const scrollToBottom = async () => {
  await nextTick()
  if (!messagesContainerRef.value) return
  messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight
}

const syncChatActiveState = (isOpen: boolean) => {
  if (!isOpen) {
    if (isFullAssistantRoute.value) return
    researchChat.setActive(false)
    return
  }

  researchChat.setActive(true)
}

const waitForSessionList = async () => {
  let attempts = 0
  while (researchChat.isLoadingSessions.value && attempts < 80) {
    await new Promise(resolve => setTimeout(resolve, 50))
    attempts++
  }
}

const ensureModeSession = async () => {
  if (researchChat.currentSession.value?.uuid) return

  if (!researchChat.isLoadingSessions.value && researchChat.sessions.value.length === 0) {
    await researchChat.loadSessions()
  }

  await waitForSessionList()

  if (researchChat.currentSession.value?.uuid) return

  const latestSessionId = researchChat.sessions.value[0]?.uuid
  if (latestSessionId) {
    await researchChat.selectSession(latestSessionId)
    return
  }

  await researchChat.createNewSession()
}

const handleSend = async () => {
  if (!canSend.value) return

  const content = messageInput.value.trim()
  messageInput.value = ''
  await ensureModeSession()
  if (!researchChat.currentSession.value?.uuid) return
  trackAssistantEvent('assistant_message_submitted', {
    mode: assistantMode,
    source: 'panel',
    webSearchEnabled: true
  })

  await researchChat.sendMessage(content, true)

  await scrollToBottom()
}

const sendPanelMessage = async (content: string) => {
  await ensureModeSession()
  if (!researchChat.currentSession.value?.uuid) return
  await researchChat.sendMessage(content, true)
}

const findRetryContent = (assistantMessageId: string): string | null => {
  const assistantIndex = displayMessages.value.findIndex(
    message => message.uuid === assistantMessageId
  )
  if (assistantIndex <= 0) return null

  for (let index = assistantIndex - 1; index >= 0; index--) {
    const candidate = displayMessages.value[index]
    if (candidate.role !== 'user') continue
    const content = candidate.content.trim()
    if (content.length > 0) return content
  }

  return null
}

const canRetryMessage = (assistantMessageId: string) => {
  return Boolean(findRetryContent(assistantMessageId))
}

const handleRetryMessage = async (assistantMessageId: string) => {
  if (isSending.value) return
  const retryContent = findRetryContent(assistantMessageId)
  if (!retryContent) return
  await sendPanelMessage(retryContent)
  await scrollToBottom()
}

const handleOpenFullScreen = async () => {
  trackAssistantEvent('assistant_handoff_clicked', {
    from: panelModeSource,
    to: assistantMode
  })
  open.value = false

  await navigateTo(Routes.ASSISTANT, {
    query: { mode: assistantMode }
  })
}

const handleSendToEditor = async (content: string) => {
  trackAssistantEvent('assistant_handoff_clicked', {
    from: panelModeSource,
    to: 'editor'
  })
  const editorQuery =
    route.name === Routes.EDITOR
      ? {
          uuid: typeof route.query.uuid === 'string' ? route.query.uuid : undefined,
          reset: typeof route.query.reset === 'string' ? route.query.reset : undefined,
          entry: typeof route.query.entry === 'string' ? route.query.entry : undefined
        }
      : undefined
  const prefillToken = storeEditorPrefill(content)
  open.value = false
  await navigateTo(Routes.EDITOR, {
    query: {
      ...(editorQuery ?? {}),
      prefillToken: prefillToken ?? undefined
    },
    hash: prefillToken ? undefined : content
  })
}

watch(
  open,
  isOpen => {
    syncChatActiveState(isOpen)
    if (!isOpen) return

    trackAssistantEvent('assistant_opened', {
      mode: assistantMode,
      source: 'panel'
    })

    researchChat.webSearchEnabled.value = true
    void ensureModeSession()
    void scrollToBottom()
  },
  { immediate: true }
)

watch(
  displayMessages,
  () => {
    if (!open.value) return
    void scrollToBottom()
  },
  { deep: true }
)

onMounted(() => {
  void nextTick(() => {
    if (!open.value) return
    void ensureModeSession()
    void scrollToBottom()
  })
})

onUnmounted(() => {
  if (isFullAssistantRoute.value) return
  researchChat.setActive(false)
})
</script>

<template>
  <Sheet v-model:open="open">
    <SheetContent
      side="right"
      class="border-border/70 bg-background/95 w-full border-l p-0 shadow-2xl sm:max-w-[440px]"
    >
      <SheetTitle class="sr-only">{{
        translateKey('featureResearch.researchChat.quickPanelTitleSr')
      }}</SheetTitle>
      <SheetDescription class="sr-only">
        {{ translateKey('featureResearch.researchChat.quickPanelDescriptionSr') }}
      </SheetDescription>

      <aside
        class="flex h-full flex-col"
        :aria-label="translateKey('featureResearch.researchChat.quickPanelAriaLabel')"
      >
        <div
          class="border-border/70 from-muted/25 to-background flex flex-col gap-3 border-b bg-gradient-to-b p-4 pr-14"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <LifeBuoy class="text-primary size-4" />
                <p class="truncate text-sm font-semibold tracking-wide uppercase">
                  {{ translateKey('assistant') }}
                </p>
              </div>
              <p class="text-muted-foreground text-xs">
                {{ translateKey('featureResearch.researchChat.quickPanelSubtitle') }}
              </p>
            </div>
            <div class="flex items-center gap-1">
              <Button
                data-testid="assistant-open-fullscreen-button"
                variant="outline"
                size="icon"
                class="border-border/70 bg-background/80 hover:bg-accent"
                :title="translateKey('featureResearch.researchChat.openFullScreen')"
                @click="handleOpenFullScreen"
              >
                <ExternalLink class="size-4" />
              </Button>
            </div>
          </div>
        </div>

        <div
          ref="messagesContainerRef"
          class="from-muted/20 via-background to-background min-h-0 flex-1 overflow-y-auto bg-gradient-to-b p-4"
        >
          <div
            v-if="!hasMessages"
            class="text-muted-foreground border-border/80 bg-card/60 rounded-xl border border-dashed p-4 text-sm leading-relaxed"
          >
            {{ translateKey('featureResearch.researchChat.quickPanelEmptyHint') }}
          </div>

          <TransitionGroup
            v-if="hasMessages"
            name="panel-message"
            tag="div"
            class="flex flex-col gap-4 pb-2"
          >
            <ResearchChatMessage
              v-for="message in displayMessages"
              :key="message.uuid"
              :message="message"
              compact
              :show-actions="true"
              :show-send-to-editor="true"
              :can-retry="canRetryMessage(message.uuid)"
              :retry-disabled="isSending"
              @send-to-editor="handleSendToEditor"
              @retry="handleRetryMessage"
            />
          </TransitionGroup>
        </div>

        <div class="border-border/70 bg-background/95 border-t p-3">
          <ChatComposerOverlay
            v-model="messageInput"
            :can-send="canSend"
            :disabled="isSending"
            :min-height="40"
            :max-height-ratio="0.28"
            :max-height-px="240"
            :placeholder="translateKey('featureResearch.researchChat.composerPlaceholder')"
            textarea-test-id="assistant-message-input"
            send-test-id="assistant-send-button"
            @submit="handleSend"
          />
        </div>
      </aside>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
.panel-message-enter-active {
  transition: all 0.2s ease-out;
}

.panel-message-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
</style>
